import { Graphics, Sprite } from 'pixi.js';

import { LIMIT_SEATS } from '../constants/constants';
import type {
  BackgroundAsset,
  ParsedSeatData,
  Point2D,
  SceneRuntime,
  SeatHoverPayload,
  SeatRenderContext,
  SeatRenderEntry,
  SeatMapRuleSet,
  SelectedSeat,
  SectorPreviewTransform,
  ViewportCameraBounds,
} from '../models/types';
import { buildSeatCentroid, buildWorldBounds, WORLD_PADDING } from './bounds';
import { setViewportCamera } from './camera';
import { buildPriceColorMap } from './colors';
import { normalizeSectorName } from './normalizeSectorName';
import { buildSeatLabelText, buildSelectedSeatPayload, drawSeatGraphic } from './render';
import { getSeatInfo, type SeatInfoIndex } from './seatData';
import {
  isSeatMapClockwiseRotationShow,
  resolveLegacySeatMapViewport,
  resolveSeatMapViewport,
} from './seatMapViewport';

const ROYAL_VIP_SEAT_ID_REGEX = /^seatcontainer\.sector\.Royal VIP(?: ?\d+)?\.row\.-\.seat\.-$/i;

const isRoyalVipFamilySector = (sectorName: string | null | undefined): boolean => {
  if (!sectorName) return false;
  const normalizedRaw = String(sectorName)
    .replace(/[–—_-]/g, ' ')
    .replace(/\s*\(.*?\)\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return /^royal vip(?: \d+)?$/.test(normalizedRaw);
};

const isLodgeSeatHiddenInReference = (seat: ParsedSeatData) => {
  const isRoyalVipSeat = ROYAL_VIP_SEAT_ID_REGEX.test(seat.id);
  const isSectorOnly = !seat.row && !seat.seat && !isRoyalVipSeat;
  const isLodge = normalizeSectorName(seat.sectorName).toLowerCase().includes('лож');
  const isRoyalVipPlaceholder = isRoyalVipFamilySector(seat.sectorName) && !isRoyalVipSeat;

  return isSectorOnly || isLodge || isRoyalVipPlaceholder;
};

type BuildSceneParams = {
  scene: SceneRuntime;
  parsedSeats: ParsedSeatData[];
  lodgeMarkerSectorNames: ReadonlySet<string>;
  seatInfoIndex: SeatInfoIndex;
  normalizeSectorName: (name?: string) => string;
  backgroundAsset: BackgroundAsset | null;
  selectedSeats: SelectedSeat[];
  activePrices: number[];
  uniquePrices: number[];
  canvasWidth: number;
  canvasHeight: number;
  rules: SeatMapRuleSet;
  showId?: number;
  isMobile: boolean;
  sectorPreviewTransform: SectorPreviewTransform;
  eventId: number;
  onSeatPointerDown: (pointerId: number, seat: SelectedSeat) => void;
  onSeatSelect: (seat: SelectedSeat) => void;
  onSeatPointerEnter: (payload: SeatHoverPayload) => void;
  onSeatPointerMove: (payload: SeatHoverPayload) => void;
  onSeatPointerOut: (payload: SeatHoverPayload) => void;
};

type BuildSceneResult = {
  renderEntries: SeatRenderEntry[];
  renderEntryBySeatId: Map<number, SeatRenderEntry[]>;
  renderEntryByPrice: Map<number, SeatRenderEntry[]>;
  normalizedOffset: Point2D;
  normalizedCenter: Point2D;
  rotationPivot: Point2D;
  cameraBounds: ViewportCameraBounds;
  minimumScale: number;
};

const clearSceneLayers = (scene: SceneRuntime) => {
  const backgroundChildren = scene.backgroundLayer.removeChildren();
  const seatChildren = scene.seatLayer.removeChildren();

  backgroundChildren.forEach(child => child.destroy());
  seatChildren.forEach(child => child.destroy());
};

export const buildScene = ({
  scene,
  parsedSeats,
  lodgeMarkerSectorNames,
  seatInfoIndex,
  normalizeSectorName,
  backgroundAsset,
  selectedSeats,
  activePrices,
  uniquePrices,
  canvasWidth,
  canvasHeight,
  rules,
  showId,
  isMobile,
  sectorPreviewTransform,
  eventId,
  onSeatPointerDown,
  onSeatSelect,
  onSeatPointerEnter,
  onSeatPointerMove,
  onSeatPointerOut,
}: BuildSceneParams): BuildSceneResult => {
  clearSceneLayers(scene);

  const { viewport, contentRoot, backgroundLayer, seatLayer, sectorLayer } = scene;

  if (backgroundAsset) {
    const sprite = new Sprite(backgroundAsset.texture);
    sprite.x = 0;
    sprite.y = 0;
    sprite.width = backgroundAsset.width;
    sprite.height = backgroundAsset.height;
    sprite.alpha = backgroundAsset.alpha ?? 0.7;
    backgroundLayer.addChild(sprite);
  }

  const renderEntries: SeatRenderEntry[] = [];
  const renderEntryBySeatId = new Map<number, SeatRenderEntry[]>();
  const renderEntryByPrice = new Map<number, SeatRenderEntry[]>();
  const priceColorMap = buildPriceColorMap(uniquePrices);
  const initialRenderContext: SeatRenderContext = {
    selectedSeatIds: new Set(selectedSeats.map(seat => seat.seatId)),
    activePriceSet: new Set(activePrices),
    hasActivePrices: activePrices.length > 0,
    hoveredRenderId: null,
  };

  for (const seat of parsedSeats) {
    const isLodgeMarker = lodgeMarkerSectorNames.has(normalizeSectorName(seat.sectorName));
    if (!isLodgeMarker && isLodgeSeatHiddenInReference(seat)) continue;

    const seatInfo = getSeatInfo(seat, seatInfoIndex, normalizeSectorName);
    const baseColor = seat.price > 0 ? (priceColorMap.get(seat.price) ?? '#ccccff') : '#ccccff';
    const graphic = new Graphics();

    if (seat.isAvailable && seatInfo) {
      graphic.eventMode = 'static';
      graphic.cursor = 'pointer';
      graphic.on('pointerdown', event => {
        const nextSelection = buildSelectedSeatPayload(seat, seatInfo, eventId);
        onSeatPointerDown(event.pointerId, nextSelection);
      });
    } else {
      graphic.eventMode = 'none';
    }

    const label = buildSeatLabelText();
    label.visible = false;
    graphic.addChild(label);

    const entry: SeatRenderEntry = {
      renderId: seat.id,
      graphic,
      label,
      seat,
      seatInfo,
      baseColor,
      isLodgeMarker,
    };

    if (seat.isAvailable && seatInfo) {
      graphic.on('pointerenter', event => {
        onSeatPointerEnter({ event, entry });
      });
      graphic.on('pointermove', event => {
        onSeatPointerMove({ event, entry });
      });
      graphic.on('pointerout', event => {
        onSeatPointerOut({ event, entry });
      });
    }

    drawSeatGraphic(graphic, entry, initialRenderContext);
    seatLayer.addChild(graphic);
    renderEntries.push(entry);

    if (seatInfo) {
      const seatEntries = renderEntryBySeatId.get(seatInfo.id) ?? [];
      seatEntries.push(entry);
      renderEntryBySeatId.set(seatInfo.id, seatEntries);

      const priceEntries = renderEntryByPrice.get(seatInfo.price) ?? [];
      priceEntries.push(entry);
      renderEntryByPrice.set(seatInfo.price, priceEntries);
    }
  }

  const seatBounds = buildWorldBounds(parsedSeats, canvasWidth, canvasHeight);
  const bounds = backgroundAsset
    ? {
        minX: Math.min(0, seatBounds.minX),
        minY: Math.min(0, seatBounds.minY),
        maxX: Math.max(backgroundAsset.width, seatBounds.maxX),
        maxY: Math.max(backgroundAsset.height, seatBounds.maxY),
      }
    : seatBounds;
  const visibleBackgroundBounds = backgroundAsset?.visibleBounds;
  const usesAdjustedViewport = isSeatMapClockwiseRotationShow(showId);
  const cameraSourceBounds = visibleBackgroundBounds
    ? {
        minX: Math.min(seatBounds.minX, visibleBackgroundBounds.minX),
        minY: Math.min(seatBounds.minY, visibleBackgroundBounds.minY),
        maxX: Math.max(seatBounds.maxX, visibleBackgroundBounds.maxX),
        maxY: Math.max(seatBounds.maxY, visibleBackgroundBounds.maxY),
      }
    : parsedSeats.length > 0
      ? seatBounds
      : bounds;
  const contentWidth = Math.max(1, bounds.maxX - bounds.minX);
  const contentHeight = Math.max(1, bounds.maxY - bounds.minY);
  const centroidBounds = usesAdjustedViewport ? cameraSourceBounds : bounds;
  const centroid = buildSeatCentroid(parsedSeats, {
    x: (centroidBounds.minX + centroidBounds.maxX) / 2,
    y: (centroidBounds.minY + centroidBounds.maxY) / 2,
  });
  const worldWidth = Math.max(1, contentWidth + WORLD_PADDING * 2);
  const worldHeight = Math.max(1, contentHeight + WORLD_PADDING * 2);
  const normalizedOffsetX = -bounds.minX + WORLD_PADDING;
  const normalizedOffsetY = -bounds.minY + WORLD_PADDING;

  backgroundLayer.position.set(normalizedOffsetX, normalizedOffsetY);
  seatLayer.position.set(normalizedOffsetX, normalizedOffsetY);
  sectorLayer.position.set(
    normalizedOffsetX + sectorPreviewTransform.x,
    normalizedOffsetY + sectorPreviewTransform.y
  );
  sectorLayer.scale.set(sectorPreviewTransform.scaleX, sectorPreviewTransform.scaleY);

  const normalizedCenterX = centroid.x + normalizedOffsetX;
  const normalizedCenterY = centroid.y + normalizedOffsetY;
  const normalizedCameraBounds = {
    minX: cameraSourceBounds.minX + normalizedOffsetX,
    minY: cameraSourceBounds.minY + normalizedOffsetY,
    maxX: cameraSourceBounds.maxX + normalizedOffsetX,
    maxY: cameraSourceBounds.maxY + normalizedOffsetY,
  };
  const normalizedBounds = {
    minX: bounds.minX + normalizedOffsetX,
    minY: bounds.minY + normalizedOffsetY,
    maxX: bounds.maxX + normalizedOffsetX,
    maxY: bounds.maxY + normalizedOffsetY,
  };
  const viewportParams = {
    rules,
    showId,
    isMobile,
    sourceBounds:
      usesAdjustedViewport || visibleBackgroundBounds ? normalizedCameraBounds : normalizedBounds,
    rotationPivot: { x: normalizedCenterX, y: normalizedCenterY },
    screenWidth: canvasWidth,
    screenHeight: canvasHeight,
  };
  const resolvedViewport = usesAdjustedViewport
    ? resolveSeatMapViewport(viewportParams)
    : resolveLegacySeatMapViewport(viewportParams);

  contentRoot.rotation = (resolvedViewport.rotationDeg * Math.PI) / 180;
  contentRoot.pivot.set(normalizedCenterX, normalizedCenterY);
  contentRoot.position.set(normalizedCenterX, normalizedCenterY);

  viewport.resize(canvasWidth, canvasHeight, worldWidth, worldHeight);
  setViewportCamera(viewport, resolvedViewport.position, resolvedViewport.scale);

  if (import.meta.env.DEV) {
    console.debug('[seat-map-viewport]', resolvedViewport.diagnostics);
  }

  return {
    renderEntries,
    renderEntryBySeatId,
    renderEntryByPrice,
    normalizedOffset: { x: normalizedOffsetX, y: normalizedOffsetY },
    normalizedCenter: { x: normalizedCenterX, y: normalizedCenterY },
    rotationPivot: { x: normalizedCenterX, y: normalizedCenterY },
    cameraBounds: resolvedViewport.cameraBounds,
    minimumScale: resolvedViewport.minimumScale,
  };
};
